import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  TextField,
  InputAdornment,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore,
  Search,
  Clear,
  ContentCopy,
  CheckCircle,
} from '@mui/icons-material';
import { SPEC_CATEGORIES, formatSpecLabel, formatSpecValue, validSpecKeys, categorizeSpecKeys } from '../utils/specFormatting';

interface SpecificationsViewerProps {
  specifications: Record<string, string> | string | null;
  cameraModel: string;
  productType?: string;
}

export const SpecificationsViewer: React.FC<SpecificationsViewerProps> = ({
  specifications,
  productType,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedSpec, setCopiedSpec] = useState<string | null>(null);

  const handleCopySpec = (key: string, value: string) => {
    const specText = `${key}: ${value}`;
    navigator.clipboard.writeText(specText);
    setCopiedSpec(key);
    setTimeout(() => setCopiedSpec(null), 2000);
  };

  // Handle different types of specifications
  const getSpecValue = (specKey: string): string | null => {
    if (!specifications || typeof specifications !== 'object') {
      return null;
    }
    // Handle both direct access and nested specs object
    const value = specifications[specKey];
    if (value && typeof value === 'string' && value.trim()) {
      return value;
    }
    return null;
  };

  // Get all specification keys from the actual data
  const allSpecKeys = validSpecKeys(specifications);

  const isAccessoryProduct = (productType || '').toLowerCase() === 'accessory';
  if (isAccessoryProduct) {
    const filteredSpecs = allSpecKeys.filter(specKey => {
      const specValue = getSpecValue(specKey);
      if (!specValue) return false;
      if (!searchQuery) return true;
      const searchLower = searchQuery.toLowerCase();
      return (
        specKey.toLowerCase().includes(searchLower) ||
        specValue.toLowerCase().includes(searchLower)
      );
    });

    const totalSpecs = allSpecKeys.length;
    const displayedSpecs = filteredSpecs.length;

    if (totalSpecs === 0) {
      return (
        <Box>
          <Typography variant="h5" gutterBottom>
            Specifications
          </Typography>
          <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
            <Typography variant="body1" color="text.secondary">
              No specifications available for this product.
            </Typography>
          </Paper>
        </Box>
      );
    }

    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Specifications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {displayedSpecs} of {totalSpecs} specifications shown
            {searchQuery && ` (filtered by "${searchQuery}")`}
          </Typography>
          <TextField
            fullWidth
            placeholder="Search specifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        </Box>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>📋</Typography>
              <Typography variant="h6">General Specifications</Typography>
              <Chip label={displayedSpecs} size="small" color="primary" variant="outlined" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "repeat(auto-fit, minmax(300px, 1fr))" }, gap: 2 }}>
              {filteredSpecs.map((specKey) => {
                const rawSpecValue = getSpecValue(specKey);
                const specValue = rawSpecValue ? formatSpecValue(specKey, rawSpecValue) : null;
                if (!specValue) return null;
                return (
                  <Box key={specKey}>
                    <Paper sx={{ p: 2, height: '100%', border: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.85rem', lineHeight: 1.2, flex: 1, mr: 1 }}>
                          {formatSpecLabel(specKey)}
                        </Typography>
                        <Tooltip title={copiedSpec === specKey ? 'Copied!' : 'Copy specification'}>
                          <IconButton size="small" onClick={() => handleCopySpec(specKey, specValue)} sx={{ ml: 1, p: 0.5 }}>
                            {copiedSpec === specKey ? <CheckCircle color="success" fontSize="small" /> : <ContentCopy fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1.3, wordBreak: 'break-word' }}>
                        {specValue}
                      </Typography>
                    </Paper>
                  </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }

  // Map specs to categories using both exact key matching and keyword matching
  // (falling back to "Other"). Shared with the product-detail Specifications
  // tab so both categorize identically.
  const categorizedSpecs = categorizeSpecKeys(allSpecKeys);

  // Filter by search query if present
  const filteredCategories = Array.from(categorizedSpecs.entries()).map(([categoryName, specKeys]) => {
    const categoryInfo = SPEC_CATEGORIES.find(cat => cat.name === categoryName);
    const filteredSpecs = specKeys.filter(specKey => {
      const specValue = getSpecValue(specKey);
      if (!specValue) return false;
      
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        return (
          specKey.toLowerCase().includes(searchLower) ||
          specValue.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
    
    return {
      name: categoryName,
      icon: categoryInfo?.icon || '📋',
      specs: filteredSpecs,
    };
  }).filter(category => category.specs.length > 0);

  const totalSpecs = allSpecKeys.length;
  const displayedSpecs = filteredCategories.reduce((sum, cat) => sum + cat.specs.length, 0);

  // If specifications is not an object, show a simple text display
  if (!specifications || typeof specifications !== 'object') {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Specifications
        </Typography>
        {specifications && typeof specifications === 'string' ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {specifications.replace(/<[^>]*>/g, '').replace(/\r\n/g, '\n')}
            </Typography>
          </Box>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
            <Typography variant="body1" color="text.secondary">
              No specifications available for this product.
            </Typography>
          </Paper>
        )}
      </Box>
    );
  }

  // If no specs found after filtering, show empty state
  if (totalSpecs === 0 || filteredCategories.length === 0) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Specifications
        </Typography>
        <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No specifications available for this product.
          </Typography>
          {searchQuery && (
            <Typography variant="body2" color="text.secondary">
              Try clearing your search to see all specifications.
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Specifications
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {displayedSpecs} of {totalSpecs} specifications shown
          {searchQuery && ` (filtered by "${searchQuery}")`}
        </Typography>
        
        {/* Search */}
        <TextField
          fullWidth
          placeholder="Search specifications..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery('')}
                >
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>

      {/* Specifications by Category */}
      {filteredCategories.map((category) => (
        <Accordion key={category.name} defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              backgroundColor: 'primary.50',
              '&:hover': {
                backgroundColor: 'primary.100',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                {category.icon}
              </Typography>
              <Typography variant="h6">
                {category.name}
              </Typography>
              <Chip
                label={category.specs.length}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "repeat(auto-fit, minmax(300px, 1fr))" }, gap: 2 }}>
              {category.specs.map((specKey) => {
                const rawSpecValue = getSpecValue(specKey);
                const specValue = rawSpecValue ? formatSpecValue(specKey, rawSpecValue) : null;
                if (!specValue) return null;

                return (
                  <Box key={specKey}>
                    <Paper
                      sx={{
                        p: 2,
                        height: '100%',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 'bold',
                            color: 'primary.main',
                            fontSize: '0.85rem',
                            lineHeight: 1.2,
                            flex: 1,
                            mr: 1,
                          }}
                        >
                          {formatSpecLabel(specKey)}
                        </Typography>
                        <Tooltip title={copiedSpec === specKey ? 'Copied!' : 'Copy specification'}>
                          <IconButton
                            size="small"
                            onClick={() => handleCopySpec(specKey, specValue)}
                            sx={{ ml: 1, p: 0.5 }}
                          >
                            {copiedSpec === specKey ? (
                              <CheckCircle color="success" fontSize="small" />
                            ) : (
                              <ContentCopy fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.8rem',
                          lineHeight: 1.3,
                          wordBreak: 'break-word',
                        }}
                      >
                        {specValue}
                      </Typography>
                    </Paper>
                  </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {filteredCategories.length === 0 && searchQuery && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No specifications found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search terms or clear the search to see all specifications.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};
